import './Toolbar.scss'
import type { ComponentProps, ReactNode } from 'react'
import { clx } from '../clx'

export interface ToolbarProps extends Omit<ComponentProps<'div'>, 'children'> {
  /** The main actions, at the start (Save, Cancel). */
  children: ReactNode
  /** Actions at the far end (a Delete), set apart from the main ones. */
  end?: ReactNode
  /**
   * `form`: under a form, with a rule above it (react-admin's form Toolbar).
   * `bare`: just the layout, for a dialog footer or a page header.
   * Default `form`.
   */
  variant?: 'form' | 'bare'
}

/**
 * react-admin's Toolbar / TopToolbar: a row of actions that wraps on narrow
 * screens, main actions first and a set-apart `end` group. Not an ARIA
 * `toolbar` (that would promise arrow-key navigation); the buttons are
 * ordinary tab stops.
 */
export function Toolbar({ children, end, variant = 'form', className, ...rest }: ToolbarProps) {
  const hasEnd = end != null && end !== false
  return (
    <div
      {...rest}
      className={clx(
        'toolbar',
        variant === 'form' && 'toolbar--as-form',
        hasEnd && 'toolbar--has-end',
        className
      )}
    >
      <div className="toolbar__main">{children}</div>
      {hasEnd && <div className="toolbar__end">{end}</div>}
    </div>
  )
}
